import React, { useState, useMemo } from 'react';
import Modal from '../Modal/Modal';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { ButtonType } from '../../types/button-type';
import { ChangeChannelAction } from '../../enums/action';
import {
  applyCurrentScalingToAllChannels,
  changeChannelActionWithReturn,
} from '../../pages/EditController/SubPages/EditChannel/channelActions';

export type MeasuredCurrentModalProps = {
  isVisible: boolean;
  onClose: () => void;
  onSend: () => void;
  currentAtClick: number;
  currentScaling: number;
  controllerId: string;
  itemNumber: number;
};

const MeasuredCurrentModal: React.FC<MeasuredCurrentModalProps> = ({
  isVisible,
  onClose,
  onSend,
  currentAtClick,
  currentScaling,
  controllerId,
  itemNumber,
}) => {
  const [measuredCurrent, setMeasuredCurrent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isApplyingToAll, setIsApplyingToAll] = useState(false);

  // Real-time calculation of new current scaling
  const newScale = useMemo(() => {
    const measuredValue = parseFloat(measuredCurrent);
    if (isNaN(measuredValue) || measuredValue <= 0 || currentAtClick === 0) return null;
    return (currentScaling * measuredValue) / currentAtClick;
  }, [measuredCurrent, currentAtClick, currentScaling]);

  const validationError = useMemo(() => {
    if (!newScale) return null;
    if (newScale * 10 < 0) return 'Value too low (min: 0)';
    if (newScale * 10 > 500) return 'Value too high (max: 500)';
    return null;
  }, [newScale]);

  const isValid = newScale !== null && validationError === null;

  const handleSend = async () => {
    if (!isValid || !newScale) return;
    setIsLoading(true);
    try {
      const success = await changeChannelActionWithReturn(
        controllerId,
        Number(itemNumber),
        ChangeChannelAction.SET_CHANNEL_CURRENT_SCALING,
        String(newScale * 10),
      );
      if (success) {
        onSend();
        setMeasuredCurrent('');
        onClose();
      } else {
        // Error handled globally
      }
    } catch (error) {
      // Error handled globally
      console.error('Error sending measured current:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyToAllChannels = async () => {
    if (!isValid || !newScale) return;
    setIsApplyingToAll(true);
    try {
      const success = await applyCurrentScalingToAllChannels(controllerId, String(newScale * 10));
      if (success) {
        onSend();
        setMeasuredCurrent('');
        onClose();
      }
    } catch (error) {
      console.error('Error applying current scaling to all channels:', error);
    } finally {
      setIsApplyingToAll(false);
    }
  };

  const handleClose = () => {
    setMeasuredCurrent('');
    onClose();
  };

  return (
    <Modal
      isVisible={isVisible}
      modalTitle="Enter measured current"
      onClose={handleClose}
      onAccept={handleSend}
      acceptBtnText={isLoading ? 'Sending...' : 'Send'}
      cancelBtnText="Cancel"
      showAcceptBtn={true}
      showCancelBtn={true}
      showCrossRoundIcon={false}
      acceptBtnDisabled={isLoading || isApplyingToAll || !isValid}
      cancelBtnDisabled={isLoading || isApplyingToAll}
    >
      <div className="flex flex-col gap-4">
        <input
          type="number"
          className="w-full rounded border border-stroke bg-gray px-4.5 py-3 text-center text-lg text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
          placeholder="Enter measured current (A)"
          value={measuredCurrent}
          onChange={(e) => setMeasuredCurrent(e.target.value)}
          disabled={isLoading || isApplyingToAll}
          min="0"
          step="0.1"
        />

        {newScale !== null && (
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              New Current Scaling: <span className="font-semibold">{newScale.toFixed(2)}</span>
            </p>
          </div>
        )}

        <InteractiveButton
          text={isApplyingToAll ? 'Applying...' : 'Apply calibration to all channels in controller'}
          type={ButtonType.PrimaryInverse}
          onClick={handleApplyToAllChannels}
          disabled={isLoading || isApplyingToAll || !isValid}
          isLoading={isApplyingToAll}
        />

        {/* Validation error display */}
        {validationError && (
          <div className="text-center">
            <p className="text-red-500 text-xs">{validationError}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MeasuredCurrentModal;
